import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { DASH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Building2, Users, Award, CalendarClock, ArrowUpRight, Eraser, CheckCircle2, AlertTriangle, XCircle, Mail } from "lucide-react";

function StatCard({ label, value, icon: Icon, tid, accent }) {
  return (
    <div data-testid={tid} className="relative bg-card border border-border rounded-lg p-5 card-hover">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
        </div>
        <div className={`w-10 h-10 grid place-items-center rounded-md ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function daysBetween(iso) {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10));
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / (1000 * 60 * 60 * 24));
}

export default function DashboardPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [purging, setPurging] = useState(false);

  const load = () => api.get("/dashboard/summary").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const purge = async () => {
    setPurging(true);
    try {
      const r = await api.post("/admin/purge?scope=all");
      const total = Object.values(r.data.purged || {}).reduce((a, b) => a + b, 0);
      toast.success(`${lang === "it" ? "Dati eliminati" : "Data purged"}: ${total}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setPurging(false);
    }
  };

  if (!data) return <div className="text-muted-foreground">{t("loading")}</div>;

  const isCliente = user?.role === "cliente";
  const dc = data.deadline_counts || {};
  const okCount = dc.ok || 0;
  const inScadCount = (dc.warning || 0) + (dc.critical || 0);
  const expiredCount = dc.expired || 0;
  const totalMonitored = okCount + inScadCount + expiredCount;
  // Health score: 100 if 0 monitored, else weighted (expired counts 2x)
  const healthScore = totalMonitored === 0
    ? 100
    : Math.max(0, Math.round(100 * okCount / (okCount + inScadCount + expiredCount * 2)));
  const healthTone = expiredCount > 0 ? "bad" : inScadCount > 0 ? "warn" : "good";
  const healthTitle = healthTone === "good"
    ? (lang === "it" ? "Tutto in regola" : "All good")
    : healthTone === "warn"
    ? (lang === "it" ? "Attenzione: scadenze imminenti" : "Attention: upcoming deadlines")
    : (lang === "it" ? "Azione richiesta: scadenze superate" : "Action required: expired deadlines");

  return (
    <div data-testid={DASH.root} className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("dashboard")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("tagline")}</p>
        </div>
        {user?.role === "super_admin" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="purge-button" className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/5 btn-lift">
                <Eraser className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Pulisci dashboard" : "Purge data"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{lang === "it" ? "Confermi eliminazione dati?" : "Confirm data purge?"}</AlertDialogTitle>
                <AlertDialogDescription>
                  {lang === "it"
                    ? "Verranno eliminati: aziende, lavoratori, scadenze, documenti, attestati, modelli, corsi, notifiche. Gli utenti restano attivi. L'operazione non è reversibile."
                    : "This will delete: companies, workers, deadlines, documents, certificates, templates, courses, notifications. Users remain active. Irreversible."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="purge-cancel">{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={purge} disabled={purging} data-testid="purge-confirm" className="bg-destructive hover:bg-destructive/90">
                  {purging ? "…" : (lang === "it" ? "Sì, elimina tutto" : "Yes, delete all")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Cliente compliance health card */}
      {isCliente && (
        <div
          data-testid="compliance-health-card"
          className={`relative overflow-hidden bg-card border rounded-lg p-6 md:p-8 ${
            healthTone === "good"
              ? "border-emerald-300/50 bg-gradient-to-br from-emerald-50 to-transparent dark:from-emerald-950/30"
              : healthTone === "warn"
              ? "border-amber-300/50 bg-gradient-to-br from-amber-50 to-transparent dark:from-amber-950/30"
              : "border-rose-300/60 bg-gradient-to-br from-rose-50 to-transparent dark:from-rose-950/30"
          }`}
        >
          <div className="flex flex-wrap items-start gap-6 justify-between">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {lang === "it" ? "Stato compliance" : "Compliance health"}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-1 flex items-center gap-3">
                {healthTone === "good" && <CheckCircle2 className="w-7 h-7 text-emerald-600" />}
                {healthTone === "warn" && <AlertTriangle className="w-7 h-7 text-amber-600" />}
                {healthTone === "bad" && <XCircle className="w-7 h-7 text-rose-600" />}
                {healthTitle}
              </h2>
              <p className="text-sm text-muted-foreground mt-1.5">
                {lang === "it"
                  ? `Punteggio salute: ${healthScore}/100 su ${totalMonitored} scadenze monitorate.`
                  : `Health score: ${healthScore}/100 over ${totalMonitored} monitored deadlines.`}
              </p>
            </div>
            <a
              href="mailto:sicurgarda@gmail.com?subject=Richiesta%20assistenza%20-%20Sicurgarda%20Scadenze"
              data-testid="contact-consultant-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 btn-lift shadow-sm"
            >
              <Mail className="w-4 h-4" />
              {lang === "it" ? "Contatta il tuo consulente" : "Contact your consultant"}
            </a>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="rounded-md border border-emerald-200/60 dark:border-emerald-800/40 bg-white/50 dark:bg-black/10 p-4">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest">{lang === "it" ? "In regola" : "OK"}</span>
              </div>
              <div className="mt-2 text-3xl font-bold" data-testid="health-ok-count">{okCount}</div>
            </div>
            <div className="rounded-md border border-amber-200/60 dark:border-amber-800/40 bg-white/50 dark:bg-black/10 p-4">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest">{lang === "it" ? "In scadenza" : "Upcoming"}</span>
              </div>
              <div className="mt-2 text-3xl font-bold" data-testid="health-warn-count">{inScadCount}</div>
            </div>
            <div className="rounded-md border border-rose-200/60 dark:border-rose-800/40 bg-white/50 dark:bg-black/10 p-4">
              <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
                <XCircle className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest">{lang === "it" ? "Scadute" : "Expired"}</span>
              </div>
              <div className="mt-2 text-3xl font-bold" data-testid="health-expired-count">{expiredCount}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t("total_companies")} value={data.totals.companies} icon={Building2} tid={DASH.cardCompanies} accent="bg-[hsl(60,8%,88%)] text-[hsl(24,10%,10%)]" />
        <StatCard label={t("total_workers")} value={data.totals.workers} icon={Users} tid={DASH.cardWorkers} accent="bg-[hsl(120,14%,20%)] text-white" />
        <StatCard label={t("total_certificates")} value={data.totals.certificates} icon={Award} tid={DASH.cardCertificates} accent="bg-[hsl(16,68%,41%)] text-white" />
        <StatCard label={t("total_deadlines")} value={data.totals.deadlines} icon={CalendarClock} tid={DASH.cardDeadlines} accent="bg-[hsl(40,88%,45%)] text-white" />
      </div>

      {/* Deadline status breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["expired", "status_expired", "badge-expired"],
          ["critical", "status_critical", "badge-critical"],
          ["warning", "status_warning", "badge-warning"],
          ["ok", "status_ok", "badge-ok"],
          ["no_term", "status_no_term", "badge-no_term"],
          ["completed", "status_completed", "badge-completed"],
        ].map(([key, label, cls]) => (
          <div key={key} className="bg-card border border-border rounded-lg p-4">
            <div className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${cls}`}>{t(label)}</div>
            <div className="mt-3 text-2xl font-bold">{data.deadline_counts?.[key] || 0}</div>
          </div>
        ))}
      </div>

      {/* Upcoming */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("upcoming_deadlines")}</h2>
          <Link to="/deadlines" className="text-xs uppercase tracking-widest text-primary inline-flex items-center gap-1 btn-lift">
            {t("view")} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <ul data-testid={DASH.upcomingList} className="divide-y divide-border">
          {data.upcoming.length === 0 && (
            <li className="p-6 text-sm text-muted-foreground">{t("no_data")}</li>
          )}
          {data.upcoming.map((d) => {
            const days = daysBetween(d.due_date);
            return (
              <li key={d.id} className={`flex items-center gap-4 px-5 py-3 severity-${d.status}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t(d.category)} · {d.due_date?.slice(0, 10)}</div>
                </div>
                <div className={`text-xs px-2 py-1 rounded badge-${d.status}`}>
                  {days < 0 ? `${t("expired_by")} ${-days} ${t("days_left")}` : `${days} ${t("days_left")}`}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
